import { TextFieldProps } from '@mui/material/TextField';
import { FilterOptionsState } from '@mui/material/useAutocomplete';
import SimpleAutoComplete from 'components/SimpleAutoComplete';
import { HOUR_IN_MILLISECONDS, PAGINATION_DEFAULT_PAGE_SIZE } from 'config';
import { i18n } from 'i18n';
import {
  PourPresetGroupDto,
  useGetPourPresetGroup,
  useGetPourPresetGroupsQuery
} from 'providers/api';
import React from 'react';

interface PourPresetGroupFieldProps {
  projectId: string;
  pourPresetGroupId?: string;
  touched?: boolean;
  error?: string
  disabledOptions?: string[];
  disabled?: boolean;
  textFieldVariant?: TextFieldProps['variant'];
  required?: boolean;
  autoFocus?: boolean;
  onChange: (pourPresetGroup?: PourPresetGroupDto | null) => void;
  onBlur?: () => void;
}

const PourPresetGroupField = ({
  projectId,
  pourPresetGroupId,
  touched,
  error, disabled,
  textFieldVariant = 'outlined',
  required,
  autoFocus,
  disabledOptions = [],
  onBlur,
  onChange,
}: PourPresetGroupFieldProps) => {
  const [open, setOpen] = React.useState(autoFocus ?? false);
  const [value, setValue] = React.useState<PourPresetGroupDto | null>(null);
  const [searchTerm, setSearchTerm] = React.useState<string | null>(null);

  const { data: pourPresetGroups, isFetched } = useGetPourPresetGroup(
    pourPresetGroupId!,
    { enabled: !!pourPresetGroupId && !value },
  );
  const initialData = isFetched ? pourPresetGroups : null;

  /**
   * If there is an initial value set it
   */
  React.useEffect(() => {
    setValue(initialData ?? null);
  }, [initialData]);

  const {
    data,
    isFetching,
  } = useGetPourPresetGroupsQuery({ page: 1, pageSize: PAGINATION_DEFAULT_PAGE_SIZE, projectId }, {
    staleTime: HOUR_IN_MILLISECONDS,
  });

  const getDisabledOptions = (group: PourPresetGroupDto) => disabledOptions.includes(group.entityId);
  const getOptionLabel = (group: PourPresetGroupDto) => group.name ?? '';
  const getOptionSelected = (group: PourPresetGroupDto, groupValue: PourPresetGroupDto) => group.entityId === groupValue.entityId;

  const filterOptions = (groups: PourPresetGroupDto[], state: FilterOptionsState<PourPresetGroupDto>) => groups
    .filter((group) => group.name?.toLocaleLowerCase().includes(state.inputValue.toLocaleLowerCase()) ?? false);

  const noOptionsText = searchTerm ? i18n._('No groups matching').concat(` "${searchTerm}"`) : i18n._('No groups available');
  const handleOnChange = (group: PourPresetGroupDto) => {
    setValue(group);
    onChange(group);
  };

  /**
   * Default options with initial data if available
   */
  const options = data?.items ?? (initialData ? [initialData!] : []);

  return (
    <SimpleAutoComplete
      label={i18n._('Pour Preset Group')}
      value={value}
      autoFocus={autoFocus}
      disabled={disabled}
      options={options}
      loading={isFetching}
      getOptionDisabled={getDisabledOptions}
      getOptionLabel={getOptionLabel}
      getOptionSelected={getOptionSelected}
      filterOptions={filterOptions}
      noOptionsText={noOptionsText}
      onChange={handleOnChange}
      onSearch={(term: string) => setSearchTerm(term)}
      onBlur={onBlur}
      touched={touched}
      error={error}
      required={required}
      open={open}
      onOpen={(isOpen: boolean) => setOpen(isOpen)}
      textFieldVariant={textFieldVariant}
    />
  );
};

export default PourPresetGroupField;
